import { useEffect, useId, useRef, useState } from "react";
import { HiArrowDownTray, HiOutlineCheckCircle } from "react-icons/hi2";
import { Folder2, ArrowLeft } from "iconsax-react";
import { FaRegFilePdf } from "react-icons/fa6";

export type DocumentDownloadOption = "projection" | "contract";

interface DocumentDownloadModalProps {
  selectedOption: DocumentDownloadOption | null;
  downloading: boolean;
  successFileName: string | null;
  error: string | null;
  title: string;
  subtitle: string;
  projectionTitle: string;
  projectionDescription: string;
  contractTitle: string;
  contractDescription: string;
  contractPending: string;
  downloadLabel: string;
  cancelLabel: string;
  successTitle: string;
  successDescription: string;
  backLabel: string;
  folderLabel: string;
  folderNotice: string;
  fileGeneratedLabel: string;
  onSelect: (option: DocumentDownloadOption) => void;
  onDownload: () => void;
  onClose: () => void;
}

export function DocumentDownloadModal({
  selectedOption,
  downloading,
  successFileName,
  error,
  title,
  subtitle,
  projectionTitle,
  projectionDescription,
  contractTitle,
  contractDescription,
  contractPending,
  downloadLabel,
  cancelLabel,
  successTitle,
  successDescription,
  backLabel,
  folderLabel,
  folderNotice,
  fileGeneratedLabel,
  onSelect,
  onDownload,
  onClose,
}: DocumentDownloadModalProps) {
  const titleId = useId();
  const firstOptionRef = useRef<HTMLButtonElement>(null);
  const [showDownloadsNotice, setShowDownloadsNotice] = useState(false);

  const handleViewDownloads = () => {
    const opened = window.open("file:///Downloads", "_blank");
    if (!opened) setShowDownloadsNotice(true);
  };

  useEffect(() => {
    firstOptionRef.current?.focus();
  }, []);

  const optionClass = (option: DocumentDownloadOption) =>
    `group flex w-full items-center gap-3 rounded-sm border px-3 py-3 text-left transition-all duration-150 ${
      selectedOption === option
        ? "border-[#007acc] bg-[#f0f8ff] shadow-[0_0_0_2px_rgba(0,122,204,0.12)]"
        : "border-[#e3eaf0] bg-white shadow-card hover:border-[#8ec7e8] hover:bg-[#f8fcff]"
    }`;

  return (
    <div
      className="fixed inset-0 z-[9998] flex items-center justify-center bg-[#003d66]/30 px-4 py-6 backdrop-blur-[2px]"
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget && !downloading) onClose();
      }}
    >
      <div
        className="w-full max-w-[25rem] overflow-hidden rounded-xl bg-white p-4 shadow-[0_18px_55px_rgba(0,61,102,0.24)] sm:p-5"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
      >
        {successFileName ? (
          <div className="flex flex-col items-center text-center">
            <div className="mb-3 flex h-11 w-11 items-center justify-center rounded-none bg-[#eaf7ff] text-[#168bc7]">
              <HiOutlineCheckCircle className="h-9 w-9" aria-hidden />
            </div>
            <h2 id={titleId} className="text-2xl font-bold text-[#003d66]">
              {successTitle}
            </h2>
            <p className="mt-1 text-xs text-[#5d7485]">{successDescription}</p>
            <div className="mt-4 flex w-full items-center gap-4 rounded-sm bg-[#F4F4F4] px-5 py-5 text-left">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-none bg-[#EEF7FC] text-[#168bc7]">
                <Folder2 size="20" color="currentColor" variant="Linear" aria-hidden />
              </span>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-normal uppercase tracking-wide text-[#05141F]">{fileGeneratedLabel}</p>
                <p className="truncate text-xs font-normal text-[#0066AA]">{successFileName}</p>
                <div className="mt-2 border-b-2 border-[#0066AA]" />
              </div>
            </div>
            <button
              type="button"
              className="mt-5 inline-flex items-center gap-1.5 rounded-sm bg-[#007acc] px-5 py-2 text-sm font-normal text-white transition-colors hover:bg-[#0066aa]"
              onClick={onClose}
            >
              <ArrowLeft size="20" color="currentColor" variant="Linear" aria-hidden />
              {backLabel}
            </button>
            <button
              type="button"
              className="mt-3 inline-flex items-center gap-1.5 text-xs font-medium text-[#557386] hover:text-[#007acc]"
              onClick={handleViewDownloads}
            >
              <Folder2 size="16" color="currentColor" variant="Linear" aria-hidden />
              {folderLabel}
            </button>
            {showDownloadsNotice ? (
              <p className="mt-1 text-[0.8125rem] text-text-muted">
                {folderNotice}
              </p>
            ) : null}
          </div>
        ) : (
          <>
            <div className="flex flex-col items-center text-center">
              <div className="mb-2 flex h-10 w-10 items-center justify-center gap-2.5 rounded-sm bg-surface-accent-soft p-2 text-[#0066AA]">
                <HiArrowDownTray className="h-7 w-9 rounded-[5px]" aria-hidden />
              </div>
              <h2 id={titleId} className="text-base font-medium text-[#104063]">
                {title}
              </h2>
              <p className="mt-1 text-[0.7rem] text-[#05141F]">{subtitle}</p>
            </div>

            <div className="my-4 h-px bg-[#e3eaf0]" />

            <div className="space-y-2" role="radiogroup" aria-label={title}>
              <button
                ref={firstOptionRef}
                type="button"
                role="radio"
                aria-checked={selectedOption === "projection"}
                className={optionClass("projection")}
                onClick={() => onSelect("projection")}
                disabled={downloading}
              >
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-none bg-[#fff0f1] text-[#ed6b73]">
                  <FaRegFilePdf className="h-5 w-5" aria-hidden />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block text-sm font-medium text-[#05141F]">{projectionTitle}</span>
                  <span className="mt-0.5 block text-[0.65rem] leading-tight text-[#104063]">{projectionDescription}</span>
                </span>
                <span className={`h-3 w-3 shrink-0 rounded-full border-2 ${selectedOption === "projection" ? "border-[#007acc] bg-[#007acc] shadow-[inset_0_0_0_2px_white]" : "border-[#9db5c4]"}`} />
              </button>

              <button
                type="button"
                role="radio"
                aria-checked={selectedOption === "contract"}
                className={optionClass("contract")}
                onClick={() => onSelect("contract")}
                disabled={downloading}
              >
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-none bg-[#eef8fd] text-[#168bc7]">
                  <Folder2 size="20" color="currentColor" variant="Linear" aria-hidden />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block text-sm font-medium text-[#05141F]">{contractTitle}</span>
                  <span className="mt-0.5 block text-[0.65rem] leading-tight text-[#104063]">{contractDescription}</span>
                  {selectedOption === "contract" && (
                    <span className="mt-1 block text-[0.65rem] font-medium text-[#b16d00]">{contractPending}</span>
                  )}
                </span>
                <span className={`h-3 w-3 shrink-0 rounded-full border-2 ${selectedOption === "contract" ? "border-[#007acc] bg-[#007acc] shadow-[inset_0_0_0_2px_white]" : "border-[#9db5c4]"}`} />
              </button>
            </div>

            {error && (
              <p className="mt-3 rounded-md bg-[#fff3f3] px-3 py-2 text-xs leading-relaxed text-[#b4232f]" role="alert">
                {error}
              </p>
            )}

            <div className="mt-4 flex items-center justify-center gap-3">
              <button
                type="button"
                className="rounded-sm border border-[#9ec9e1] bg-white px-5 py-2 text-xs font-medium text-[#104063] transition-colors hover:bg-[#f0f8ff]"
                onClick={onClose}
                disabled={downloading}
              >
                {cancelLabel}
              </button>
              <button
                type="button"
                className={`rounded-sm px-5 py-2 text-xs font-semibold transition-colors ${
                  selectedOption && !downloading
                    ? "bg-[#007acc] text-white hover:bg-[#0066aa]"
                    : "cursor-not-allowed bg-[#e7edf1] text-[#9aabb5]"
                }`}
                onClick={onDownload}
                disabled={!selectedOption || downloading}
              >
                {downloading ? "..." : downloadLabel}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
